import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, type IkDizin, type Me } from './api';
import { ORTAK, rolAdi, tamSayi, tarih, ROL_ADI } from './metin/ortak';
import { IK_METIN } from './metin/ik';

/**
 * İK — beşinci bölüm (Ürün Sahibi, 30.08.2026).
 *
 * *"CRM'e 5. modülü getir, HR modülü, kullanıcıların tamamını görsün sistemdeki."*
 *
 * ## Bu ekranın yapmadığı şey
 *
 * Tek bir düğmesi yok. Rol seçici yok, "kapat" yok, "parola koy" yok. `/kullanicilar` ekranında
 * hepsi var ve orada kalıyorlar, çünkü hesap açmak bir **erişim kararı** ve bu ekranın izni
 * (`hr:directory_read`) o kararı vermiyor. Ekranın alt notu bunu yazıyor: arayıp bulamayan
 * kişiye bir cevap vermek, sessizce eksik bırakmaktan iyidir.
 *
 * ## Süzme ve sıralama neden sunucuda
 *
 * Türkçe iki tuzak taşıyor ve ikisi de sessiz. `toLowerCase()` "İ" harfinde yanlış katlıyor,
 * ve bu kurulumun veritabanı harmanlaması `C.UTF-8` olduğu için "Şule" bayt sırasına göre
 * listenin dibine düşüyor. İkisinin de çözümü uçta (`api/src/modules/hr/hr.service.ts`); ekran
 * kendi başına ikinci bir sıralama yapmıyor ki iki yerde iki farklı sıra oluşmasın.
 *
 * ## İzin denetimi burada
 *
 * Panelin kendisinde, çağıranda değil. Sekmeyi gizlemek yetkilendirme değil ve hiç olmadı;
 * `/hr/directory` sunucuda yeniden kontrol ediliyor ve izinsiz istek 403 dönüyor.
 */

/** Sıralanabilir sütunlar: uçtaki `DIZIN_SIRALAMA` ile aynı liste olmak zorunda. */
const SUTUNLAR = [
  { anahtar: 'displayName', etiket: IK_METIN.dizin.sutunAd },
  { anahtar: 'email', etiket: IK_METIN.dizin.sutunEposta },
  { anahtar: 'role', etiket: IK_METIN.dizin.sutunRol },
  { anahtar: 'isActive', etiket: IK_METIN.dizin.sutunDurum },
  { anahtar: 'lastLoginAt', etiket: IK_METIN.dizin.sutunSonGiris },
  { anahtar: 'createdAt', etiket: IK_METIN.dizin.sutunAcilis },
] as const;

/** Rol süzgecinin seçenekleri. Kod değeri gider, insanın okuduğu ad görünür. */
const ROL_SECENEKLERI = Object.keys(ROL_ADI);

function hataMetni(caught: unknown, varsayilan: string): string {
  if (caught instanceof ApiError) return caught.message;
  return varsayilan;
}

function Dizin() {
  const [dizin, setDizin] = useState<IkDizin | null>(null);
  const [arama, setArama] = useState('');
  const [durum, setDurum] = useState('ALL');
  const [rol, setRol] = useState('');
  const [sira, setSira] = useState<string>('displayName');
  const [yon, setYon] = useState<'asc' | 'desc'>('asc');
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const metin = IK_METIN.dizin;

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      setDizin(await api.ikDizin({ search: arama.trim(), status: durum, role: rol, sort: sira, direction: yon }));
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    } finally {
      setYukleniyor(false);
    }
  }, [arama, durum, rol, sira, yon, metin.hata]);

  // Arama kutusuna her harfte bir istek atılmıyor: 220 ms, müşteri listesindeki değerin aynısı.
  useEffect(() => {
    const zaman = window.setTimeout(() => void yukle(), 220);
    return () => window.clearTimeout(zaman);
  }, [yukle]);

  /** Bir başlığa tıklamak: aynı sütunsa yönü çevirir, başka sütunsa artan sıradan başlar. */
  const sirala = (anahtar: string) => {
    if (anahtar === sira) {
      setYon((onceki) => (onceki === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSira(anahtar);
    setYon('asc');
  };

  const ozet = dizin?.summary;
  const rolDagilimi = useMemo(
    () => (ozet ? ozet.byRole.map((satir) => `${rolAdi(satir.role)} ${tamSayi(satir.count)}`).join(' · ') : ''),
    [ozet],
  );

  return (
    <section id="ik-dizin" className="content-section" data-testid="ik-dizin" aria-labelledby="ik-dizin-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="ik-dizin-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <span className="count-badge" data-testid="ik-dizin-sayi">
          {tamSayi(dizin?.shown ?? 0)} {metin.kisiEki}
        </span>
      </div>

      {hata ? (
        <div className="error callout" role="alert" data-testid="ik-hata">
          <span>{hata}</span>
          <button type="button" onClick={() => void yukle()}>{ORTAK.yeniden}</button>
        </div>
      ) : null}

      {/*
        Kadro özeti tablonun **üstünde** duruyor ve hiçbir süzgeçten etkilenmiyor: "kaç kişiyiz"
        sorusunun cevabı, arama kutusuna yazılan kelimeye ya da seçilen duruma göre değişmemeli.
        Uç da bu yüzden sayımları veritabanında ve süzgeçten bağımsız yapıyor.

        **`.callout` kullanılmıyor ve sebebi ölçüldü (30.08.2026, 1440 piksel ekran görüntüsü).**
        O sınıfın taban rengi `--danger`: kırmızı kenarlık, pembe zemin. Kadro sayımı bir alarm
        değil, bir bilgi; kırmızı bir kutuda duran "Toplam 10" ekranı okuyan kişiye bir sorun
        olduğunu söylüyordu. `callout-ok` da doğru değil — yeşil "onay" demek ve burada
        onaylanan bir şey yok. Nötr metin sınıfları kullanıldı; bu bloğun görsel biçimi Codex'in
        kararı ve teslim belgesinde açık madde olarak duruyor.
      */}
      {ozet ? (
        <div className="ik-ozet" data-testid="ik-ozet">
          <p>
            <strong>{metin.ozetBaslik}:</strong> {metin.ozetToplam} {tamSayi(ozet.total)} · {metin.ozetEtkin}{' '}
            {tamSayi(ozet.active)} · {metin.ozetPasif} {tamSayi(ozet.inactive)} · {metin.ozetHicGiris}{' '}
            {tamSayi(ozet.neverLoggedIn)}
          </p>
          <p className="source">
            {metin.ozetRolDagilimi}: {rolDagilimi}
          </p>
        </div>
      ) : null}

      {dizin?.truncated ? (
        <div className="callout" role="status" data-testid="ik-tavan">
          <span>{metin.tavan(dizin.shown)}</span>
        </div>
      ) : null}

      <div className="toolbar">
        <label className="search-field">
          <span>{metin.araBaslik}</span>
          <input
            data-testid="ik-arama"
            placeholder={metin.araIpucu}
            value={arama}
            maxLength={80}
            onChange={(olay) => setArama(olay.target.value)}
          />
        </label>
        <label>
          <span className="muted">{metin.sutunRol}</span>{' '}
          <select data-testid="ik-rol-suzgeci" value={rol} onChange={(olay) => setRol(olay.target.value)}>
            <option value="">{metin.rolHepsi}</option>
            {ROL_SECENEKLERI.map((deger) => (
              <option key={deger} value={deger}>
                {rolAdi(deger)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="muted">{metin.sutunDurum}</span>{' '}
          <select data-testid="ik-durum-suzgeci" value={durum} onChange={(olay) => setDurum(olay.target.value)}>
            <option value="ALL">{metin.durumHepsi}</option>
            <option value="ACTIVE">{metin.durumEtkinler}</option>
            <option value="INACTIVE">{metin.durumPasifler}</option>
          </select>
        </label>
      </div>

      <div className="table-wrap">
        <table data-testid="ik-tablo">
          <thead>
            <tr>
              {SUTUNLAR.map((sutun) => (
                <th key={sutun.anahtar} aria-sort={sira === sutun.anahtar ? (yon === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  {/*
                    Başlık gerçek bir düğme: klavyeyle odaklanabiliyor ve ekran okuyucu onu
                    tıklanabilir olarak okuyor. Ok işareti yalnız etkin sütunda, çünkü her
                    başlıkta duran bir ok hangisinin etkin olduğunu söylemiyor.
                  */}
                  <button
                    type="button"
                    className="link-button"
                    data-testid={`ik-sirala-${sutun.anahtar}`}
                    aria-label={metin.siralaEtiketi(sutun.etiket)}
                    onClick={() => sirala(sutun.anahtar)}
                  >
                    {sutun.etiket}
                    {sira === sutun.anahtar ? (yon === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dizin?.items.map((satir) => (
              <tr key={satir.id} data-testid="ik-satir">
                <td>{satir.displayName}</td>
                <td>{satir.email}</td>
                <td>{rolAdi(satir.role)}</td>
                <td>
                  {/* Sınıf adı kod değerinden geliyor; ekrandaki yazı karşılığı. */}
                  <span className={`state state-${satir.isActive ? 'active' : 'inactive'}`}>
                    {satir.isActive ? metin.durumEtkin : metin.durumPasif}
                  </span>
                </td>
                {/* Boş bir tarih hücresi "bilgi yok" der; bu kelime "hiç girmedi" der. */}
                <td data-testid={`ik-son-giris-${satir.id}`}>{satir.lastLoginAt ? tarih(satir.lastLoginAt) : metin.hicGiris}</td>
                <td>{tarih(satir.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!yukleniyor && dizin?.items.length === 0 ? (
          <div className="empty-state">
            <strong>{metin.bosBaslik}</strong>
            <span>{metin.bosTarif}</span>
          </div>
        ) : null}
      </div>

      <p className="source">{metin.yetkiNotu}</p>
    </section>
  );
}

/**
 * İK sayfası.
 *
 * `me.permissions` kontrolü burada, çünkü ekranın kendi kapısı olmalı: adresi elle yazan biri
 * için de aynı cevap dönüyor. Sunucu zaten ikinci kez soruyor.
 */
export function IkPaneli({ me }: { me: Me }) {
  return me.permissions.includes('hr:directory_read') ? <Dizin /> : null;
}
